import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Tabs,
  Tab,
  Chip,
  Stack,
} from '@mui/material';
import styles from '../styles/components/InsuranceShowcase.module.css';

export type ShowcaseProduct = {
  id: string;
  company: string;
  name: string;
  description: string;
  features: string[];
  imageUrl: string;
  price: string;
  tag?: string;
};

export type ProductShowcaseProps = {
  title: string;
  products: ShowcaseProduct[];
  learnMoreLabel: string;
};

const ProductShowcase: React.FC<ProductShowcaseProps> = ({
  title,
  products,
  learnMoreLabel,
}) => {
  const [selectedCompany, setSelectedCompany] = React.useState(0);

  const companies = products
    .map((product) => product.company)
    .filter((company, index, self) => self.indexOf(company) === index);

  const handleCompanyChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedCompany(newValue);
  };

  return (
    <Paper elevation={0} className={styles.container}>
      <Typography variant="h4" gutterBottom className={styles.title}>
        {title}
      </Typography>

      <Tabs
        value={selectedCompany}
        onChange={handleCompanyChange}
        variant="scrollable"
        scrollButtons="auto"
        className={styles.tabs}
      >
        {companies.map((company, index) => (
          <Tab key={index} label={company} />
        ))}
      </Tabs>

      <Grid container spacing={4}>
        {products
          .filter((product) => product.company === companies[selectedCompany])
          .map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card className={styles.card}>
                {product.tag && (
                  <Chip label={product.tag} color="secondary" className={styles.tag} />
                )}
                <CardMedia
                  component="img"
                  height="200"
                  image={product.imageUrl}
                  alt={product.name}
                  className={styles.cardMedia}
                />
                <CardContent className={styles.cardContent}>
                  <Typography variant="h5" className={styles.productName}>
                    {product.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {product.description}
                  </Typography>
                  <Stack className={styles.featureList}>
                    {product.features.map((feature, index) => (
                      <Box key={index} className={styles.featureItem}>
                        <Box className={styles.featureDot} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Box className={styles.priceContainer}>
                    <Typography variant="h6" className={styles.price}>
                      {product.price}
                      <Typography component="span" variant="body2" className={styles.priceUnit}>
                        /month
                      </Typography>
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      className={styles.learnMoreButton}
                    >
                      {learnMoreLabel}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Paper>
  );
};

export default ProductShowcase;
